"use client";

import { useMemo, useState } from "react";
import { Article, ArticleCategory } from "@/data/articles";
import { ArticleCard } from "./ArticleCard";
import Link from "next/link";
import { ArrowLeft } from "lucide-react";

const LEAF_SVG = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'><path d='M60 10 C40 30 40 60 60 80 C80 60 80 30 60 10 Z M60 40 L60 110' stroke='%23166534' stroke-width='1.2' fill='none'/></svg>")`;

type Filter = "All" | ArticleCategory;

interface Props {
  initialArticles: Article[];
  categories: ArticleCategory[];
}

export function ArticlesClient({ initialArticles, categories }: Props) {
  const filters: Filter[] = ["All", ...categories];
  const [filter, setFilter] = useState<Filter>("All");

  const visible = useMemo(() => {
    if (filter === "All") return initialArticles;
    return initialArticles.filter((a) => a.category === filter);
  }, [filter, initialArticles]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-healing-50/40 via-white to-white mt-20">
      {/* Hero */}
      <header className="relative overflow-hidden border-b border-healing-100">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{ backgroundImage: LEAF_SVG, backgroundSize: "180px" }}
        />
        <div className="container mx-auto px-4 py-16 lg:py-24 text-center relative">
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-sm text-healing-700 hover:text-healing-900 mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>
          <p className="text-xs uppercase tracking-[0.3em] text-healing-600 font-semibold mb-4">
            OICNM Journal
          </p>
          <h1 className="font-serif-display text-4xl md:text-6xl text-healing-900 leading-tight mb-5">
            Insights &amp; Articles
          </h1>
          <p className="max-w-2xl mx-auto text-base md:text-lg text-muted-foreground leading-relaxed">
            Reflections, research, and practical wisdom from the Olive
            International College of Natural Medicine — for clinicians,
            students, and anyone walking the path of holistic health.
          </p>

          {/* Category filter pills */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-2 md:gap-3">
            {filters.map((f) => {
              const active = filter === f;
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  aria-pressed={active}
                  className={[
                    "px-4 md:px-5 py-2 rounded-full text-sm font-medium border transition-all duration-300",
                    active
                      ? "bg-healing-700 text-white border-healing-700 shadow-md"
                      : "bg-white text-healing-800 border-healing-200 hover:border-healing-400 hover:bg-healing-50",
                  ].join(" ")}
                >
                  {f}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Article grid */}
      <main className="container mx-auto px-4 py-14 lg:py-20">
        {visible.length === 0 ? (
          <p className="text-center text-muted-foreground py-20">
            No articles in this category yet — check back soon.
          </p>
        ) : (
          <div
            key={filter}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 animate-fade-in"
          >
            {visible.map((a) => (
              <ArticleCard
                key={a.slug}
                article={a}
                variant={a.featured ? "featured" : "default"}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
